import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import debounce from 'lodash-es/debounce'
import isEmpty from 'lodash-es/isEmpty'
import { useMemo } from 'react'
import { getLastPosition, getNextPosition, getPreviousPosition } from '../../../../chess/gameTree'
import PopoverWithContent from '../../../../components/PopoverWithContent/PopoverWithContent'
import { useGlobalContext } from '../../../../sharedComponents/src/Contexts/GlobalContext'
import { GroupNames } from '../../../../sharedComponents/src/globalHeader/GlobalHeader'
import { useScreenSize } from '../../../../sharedComponents/src/hooks/useScreenSize'
import { useStoreActions, useStoreState } from '../../../../store/hooks'
import CircularButton from '../../../analysisView/components/analyisControls/CircularButton'
import SolutionIcon from '../../../PuzzlesView/icons/Solution'
import BackIcon from '../../icons/Back'
import CalendarIcon from '../../icons/Calendar'
import HintIcon from '../../icons/Hint'
import NextIcon from '../../icons/Next'
import DailyPuzzleCalendar from './DailyPuzzleCalendar'

const DailyPuzzleControls: React.FC = () => {
    const puzzle = useStoreState((state) => state.dailyPuzzleViewV2.puzzle)
    const gameTree = useStoreState((state) => state.dailyPuzzleViewV2.gameTree)
    const availableHints = useStoreState((state) => state.dailyPuzzleViewV2.availableHints)
    const showHintLimit = useStoreState((state) => state.dailyPuzzleViewV2.showHintLimit)
    const customArrows = useStoreState((state) => state.dailyPuzzleViewV2.customArrows)
    const puzzleState = useStoreState((state) => state.dailyPuzzleViewV2.puzzleState)
    const currentPosition = useStoreState((state) => state.dailyPuzzleViewV2.currentPosition)
    const showSolution = useStoreState((state) => state.dailyPuzzleViewV2.showSolution)
    const notations = useStoreState((state) => state.dailyPuzzleViewV2.notations)

    const handleHint = useStoreActions((state) => state.dailyPuzzleViewV2.handleHint)
    const autoMove = useStoreActions((state) => state.dailyPuzzleViewV2.autoMove)
    const setCurrentPosition = useStoreActions((state) => state.dailyPuzzleViewV2.setCurrentPosition)

    const { userType } = useGlobalContext()
    const { isMobile } = useScreenSize()

    const hasNextMoves = currentPosition.nextMoveIds.length > 0
    const hasPrevMoves = currentPosition.previousMoveId !== undefined
    const disableByState = puzzleState !== null
    const isInitialized = !Boolean(notations.length)
    const disableByMoveList = useMemo(() => currentPosition.id !== getLastPosition(gameTree).id, [currentPosition.id])

    const handleShowSolution = () => {
        autoMove(true)
    }

    const debouncedHandleHint = useMemo(() => {
        return debounce(
            () => {
                handleHint(userType)
            },
            500,
            { leading: true, trailing: false },
        )
    }, [handleHint, userType])

    const onBackMove = () => {
        const position = getPreviousPosition(gameTree, currentPosition.id)
        if (!position) return
        setCurrentPosition(position)
    }

    const onNextMove = () => {
        const position = getNextPosition(gameTree, currentPosition.id)
        if (!position) return
        setCurrentPosition(position)
    }

    const hintText =
        userType === GroupNames.SUBSCRIBED && availableHints === 0 ? 'Earn more from Daily Bonus' : undefined

    return (
        <Stack direction="row" gap="16px">
            <PopoverWithContent showOnClick={!(isEmpty(puzzle) || showSolution)} content={<DailyPuzzleCalendar />}>
                <CircularButton
                    onClick={() => {}}
                    title="Calendar"
                    icon={<CalendarIcon date={puzzle?.date} />}
                    removeBorder
                    disabled={isEmpty(puzzle) || showSolution}
                />
            </PopoverWithContent>
            <Divider orientation="vertical" flexItem sx={{ borderColor: 'text.primary', my: 0.75 }} />
            {puzzleState === true && isMobile ? (
                <CircularButton
                    onClick={onBackMove}
                    title="Back"
                    disabled={!hasPrevMoves}
                    removeBorder
                    icon={<BackIcon />}
                />
            ) : (
                <CircularButton
                    onClick={handleShowSolution}
                    title="Solution"
                    icon={<SolutionIcon />}
                    disabled={disableByState || isEmpty(puzzle) || showSolution || isInitialized || disableByMoveList}
                />
            )}
            {puzzleState === true && isMobile ? (
                <CircularButton
                    onClick={onNextMove}
                    title="Next"
                    disabled={!hasNextMoves}
                    removeBorder
                    icon={<NextIcon />}
                />
            ) : (
                <CircularButton
                    onClick={debouncedHandleHint}
                    title="Hint"
                    description={hintText}
                    icon={<HintIcon />}
                    badge={availableHints >= 0 ? availableHints : undefined}
                    animated={customArrows.length === 0}
                    disabled={disableByState || isEmpty(puzzle) || showSolution || isInitialized || disableByMoveList}
                    showTooltip={showHintLimit}
                />
            )}
        </Stack>
    )
}

export default DailyPuzzleControls
